import { createTheme } from '@mui/material/styles';

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#FFD700', // Gold - matches T1 logo gold variant
      light: '#FFE135',
      dark: '#B8950A',
    },
    secondary: {
      main: '#1A1A1A', // Deep black - matches T1 logo black variant
      light: '#2D2D2D',
      dark: '#000000',
    },
    background: {
      default: '#0A0E14', // Keep the sophisticated dark background
      paper: '#1C1C1C', // Slightly lighter for contrast with logos
    },
    text: {
      primary: '#FFFFFF', // White - matches T1 logo white variants
      secondary: '#B8BCC8',
    },
    success: {
      main: '#00D4AA', // Keep the trading success color
    },
    error: {
      main: '#FF6B6B', // Keep the trading alert color
    },
    warning: {
      main: '#FFD700', // Use gold for warnings to maintain brand consistency
    },
    info: {
      main: '#42A5F5',
    },
    // Custom brand colors for T1 FX Team
    t1Brand: {
      gold: '#FFD700',
      darkGold: '#B8950A',
      lightGold: '#FFE135',
      black: '#1A1A1A',
      white: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '3.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.6,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          padding: '10px 24px',
        },
        containedPrimary: {
          background: 'linear-gradient(45deg, #FFD700, #B8950A)',
          color: '#000000',
          '&:hover': {
            background: 'linear-gradient(45deg, #B8950A, #996F08)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
          border: '1px solid rgba(255, 215, 0, 0.1)', // Subtle gold border
          backgroundColor: '#1C1C1C',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(10, 14, 20, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255, 215, 0, 0.2)', // Gold accent border
        },
      },
    },
  },
});

// Extend the theme interface to include custom brand colors
declare module '@mui/material/styles' {
  interface Palette {
    t1Brand: {
      gold: string;
      darkGold: string;
      lightGold: string;
      black: string;
      white: string;
    };
  }

  interface PaletteOptions {
    t1Brand?: {
      gold?: string;
      darkGold?: string;
      lightGold?: string;
      black?: string;
      white?: string;
    };
  }
} 